import React, { useState } from 'react';

const Section = ({ title, children, defaultOpen }) => {
  const [open, setOpen] = useState(defaultOpen || false);
  return (
    <div style={{ marginBottom: 8, border: '1px solid #e0e0e0', borderRadius: 4 }}>
      <div onClick={() => setOpen(!open)} style={{ padding: '10px 14px', cursor: 'pointer', background: open ? '#f5f5f5' : '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontWeight: 600, fontSize: '0.85rem' }}>{title}</span>
        <span style={{ color: '#666', fontSize: '0.75rem' }}>{open ? '▼' : '▶'}</span>
      </div>
      {open && <div style={{ padding: '8px 14px 14px', fontSize: '0.82rem', lineHeight: 1.7 }}>{children}</div>}
    </div>
  );
};

const Table = ({ headers, rows }) => (
  <table style={{ fontSize: '0.8rem', borderCollapse: 'collapse', width: '100%', marginTop: 8 }}>
    <thead><tr style={{ background: '#232f3e', color: '#fff' }}>{headers.map(h => <th key={h} style={{ padding: '5px 8px', textAlign: 'left' }}>{h}</th>)}</tr></thead>
    <tbody>{rows.map((row, i) => <tr key={i} style={{ borderBottom: '1px solid #eee' }}>{row.map((cell, j) => <td key={j} style={{ padding: '4px 8px' }}>{cell}</td>)}</tr>)}</tbody>
  </table>
);

export default function FAQ() {
  const [activeTab, setActiveTab] = useState('all');
  const tabs = [
    { key: 'all', label: 'All' },
    { key: 'hourly', label: 'Hourly' },
    { key: 'meesa', label: 'Bottoms Up Forecast' },
    { key: 'lvp', label: 'LVP' },
    { key: 'midi', label: 'MIDI' },
    { key: 'msm', label: 'MSM' },
    { key: 'fallin', label: 'FALLIN' },
    { key: 'binks', label: 'Vessel Forecast' },
    { key: 'history', label: 'History' },
    { key: 'infra', label: 'Infrastructure' },
    { key: 'laborcodes', label: 'Labor Tracking Codes' },
  ];

  const show = (tab) => activeTab === 'all' || activeTab === tab;

  return (
    <div>
      <div style={{ textAlign: 'center', padding: '18px 12px', marginBottom: 16, background: '#f0f6ff', border: '1px solid #d6e4ff', borderRadius: 6 }}>
        <div style={{ fontSize: '1.2rem', fontWeight: 700, color: '#232f3e' }}>
          Proudly made by Jenna (@jenbre) and Dan Dan (@drodehea), go Mariners, go Seahawks!
        </div>
        {/* DevSpace Daily Startup Guide link hidden — kept, not deleted. Flip to true to restore. */}
        {false && (
        <a
          href="https://quip-amazon.com/jnOhAxNQhDM3/JAR-JAR-DevSpace-Daily-Startup-Guide"
          target="_blank"
          rel="noopener noreferrer"
          style={{ display: 'inline-block', marginTop: 8, fontSize: '0.9rem', color: '#0972d3', textDecoration: 'underline' }}
        >
          JAR JAR DevSpace Daily Startup Guide
        </a>
        )}
        <div style={{ marginTop: 10, fontSize: '0.9rem', color: '#232f3e' }}>
          Please reach out directly if you have any questions
        </div>
      </div>
      {/* FAQ content hidden for now (outdated) — kept, not deleted. Flip to true to restore. */}
      {false && (<>
      <h2 className="section-title">FAQ &amp; Feature Guide</h2>
      <div style={{ display: 'flex', gap: 4, marginBottom: 16, flexWrap: 'wrap' }}>
        {tabs.map(t => (
          <button key={t.key} className={`btn ${activeTab === t.key ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setActiveTab(t.key)} style={{ fontSize: '0.7rem', padding: '4px 10px' }}>{t.label}</button>
        ))}
      </div>

      {/* HOURLY TAB */}
      {show('hourly') && (
        <div className="card" style={{ marginBottom: 12 }}>
          <h3 style={{ fontSize: '0.9rem', margin: '0 0 8px', color: '#1565c0' }}>📊 Hourly Tab</h3>
          <Section title="Where do PID/AROS carton counts come from?">
            <p><strong>MonitorPortal</strong> (AFTInboundDirectorService) is the source of truth for PID and AROS carton counts. The sync loop pulls per-minute data from the MWS CSV endpoint every 5 minutes and merges it into the FCLM cache.</p>
            <p>FCLM Function Rollup (LP Receive process) is used as a fallback only if MonitorPortal auth expires.</p>
            <p><strong>Note:</strong> The current in-progress hour uses FCLM's live number (MonitorPortal has reporting lag for partial hours). Once an hour completes, MonitorPortal's final total is used.</p>
          </Section>
          <Section title="Why might the current hour show lower numbers than MonitorPortal?">
            <p>The current (in-progress) hour is intentionally NOT overridden with MonitorPortal data because MP only has a partial snapshot while the hour is still running. Once the hour ends, the next sync cycle (every 5 min) picks up the full/final MonitorPortal total.</p>
          </Section>
          <Section title="Green/red coloring — now vs the LVP plan for the shift">
            <p>Per-function cell shading and the Department Throughput <strong>% to LP</strong> compare each hour's actual CPLH against the <strong>LVP plan rate for that shift</strong> (not a hardcoded standard).</p>
            <p><strong>Plan rate = planned cartons for the path ÷ that function's planned (computed) hours</strong>, using the LVP plan's <code>computedHours</code> for the shift. FCLM process-path names are mapped to the LVP function names (e.g. Wrap/Lantech → Pallet Wrap/Lantech, Offline Wallbuild → Wall Builder).</p>
            <p>The old hardcoded rates remain only as a fallback if no LVP plan is saved for that shift. This is why the separate <strong>Rates tab was retired</strong> — the hourly is now driven by the LVP.</p>
          </Section>
        </div>
      )}

      {/* MEESA TAB */}
      {show('meesa') && (
        <div className="card" style={{ marginBottom: 12 }}>
          <h3 style={{ fontSize: '0.9rem', margin: '0 0 8px', color: '#1565c0' }}>📈 Bottoms Up Forecast Tab</h3>
          <Section title="How Bottoms Up Forecast calculates forecasted hours" defaultOpen={activeTab === 'meesa'}>
            <p><strong>Hours = Volume ÷ Forecast Rate</strong> (F1 week rate from top-3-of-4 baseline + ramp %)</p>
            <h4 style={{ marginTop: 12 }}>Volume Flow</h4>
            <ul>
              <li><strong>Total IB Cartons</strong> = manual input</li>
              <li><strong>PID %</strong> → PID Cartons = Total IB × PID%</li>
              <li><strong>AROS %</strong> → AROS Cartons = Total IB × AROS%</li>
              <li><strong>PR %</strong> = 100% - PID% - AROS%</li>
              <li><strong>EOL %</strong> (of PID) → EOL = PID × EOL%</li>
              <li><strong>Fluid Direct</strong> = PID × (1 - EOL%)</li>
              <li><strong>Fluid Offline</strong> = EOL + AROS</li>
            </ul>
          </Section>
          <Section title="FCLM function name aliases (Fluid Load, OB TDR, etc.)">
            <p>FCLM's weekly Function Rollup report recently started emitting newer labels for some functions. JAR JAR normalizes these back to the historical canonical names:</p>
            <Table headers={['FCLM Raw Name', 'Normalized To']} rows={[
              ['Fluid Load', 'Fluid Load - Case'],
              ['OB TDR', 'TransferOut DockCrew'],
              ['OB EPJ WaterSpider', 'Outbound Dock Crew'],
            ]} />
          </Section>
          <Section title="Summary Line Item → Carton Source">
            <Table headers={['Line Item', 'Carton Source']} rows={[
              ['Receive Dock', 'Total IB Cartons'],
              ['Receive Support', 'Total IB Cartons'],
              ['Transfer Out', 'Total OB Cartons'],
              ['Transfer Out Dock', 'Total OB Cartons'],
              ['Total Inbound', 'Total IB Cartons'],
              ['THROUGHPUT', 'Total IB + Total OB'],
              ['Admin/HR/IT', 'Total IB + Total OB'],
            ]} />
          </Section>
          <Section title="Complete weeks only (current partial week hidden)">
            <p>Bottoms Up Forecast and Tops Down Forecast hide the <strong>current in-progress (partial) week</strong> from the view and all calcs, so a half-finished week never drags rates down. The default range shows the <strong>last 4 complete weeks</strong>.</p>
          </Section>
          <Section title="HWM is historic (not the selected timeframe)">
            <p>The <strong>% to HWM</strong> plan mode uses the high-water-mark week across <strong>all historical YTD weeks</strong>, not just the weeks currently displayed. So the HWM target is stable even when the peak week isn't on screen.</p>
          </Section>
        </div>
      )}

      {/* LVP TAB */}
      {show('lvp') && (
        <div className="card" style={{ marginBottom: 12 }}>
          <h3 style={{ fontSize: '0.9rem', margin: '0 0 8px', color: '#1565c0' }}>📋 LVP Tab</h3>
          <Section title="Admin Hours Detail Table">
            <p>The Admin Hours Detail table at the bottom of LVP shows per-function admin hours by shift. Values are editable and auto-sum into the Admin Hours row above.</p>
            <p><strong>Categories:</strong> Support Staff (white), HR (purple), Learning (blue), Safety (green), TOM (orange), Other (white)</p>
            <p><strong>TOM hours are excluded</strong> from the Admin Hours total — they're tracked separately. TOM still displays in the detail table for visibility.</p>
            <p><strong>Drag-across (⟹):</strong> Copies Sunday Day value across all days, Sunday Night across all nights.</p>
            <p><strong>Data source:</strong> 4-week averages from FCLM Function Rollup (Process 01002960 - Admin/HR/IT). Resets to defaults when you switch weeks.</p>
          </Section>
          <Section title="CSV Export">
            <p>The <strong>📊 Export Hours</strong> button exports all production dept/function hours AND admin category/function hours in one CSV, broken by shift. Same column structure as the main table.</p>
          </Section>
          <Section title="How Diluted CPLH is calculated">
            <p><strong>Diluted CPLH</strong> = (Cartons × 2) ÷ (IB Hours + DA Hours + Admin Hours)</p>
            <p>Admin Hours = sum of all non-TOM categories from the detail table.</p>
          </Section>
          <Section title="⚡ Most Efficient Execution button">
            <p>Rebalances the per-shift carton <strong>mix</strong> (PID/Sorter ↔ AROS ↔ PR) to <strong>flatten Extra Hours as a % of Expected</strong> across all 14 shifts.</p>
            <p><strong>Held constant:</strong> each shift's TOTAL cartons and each path's WEEKLY total. SKID stays static. Moves are paired — labor-heavy AROS/PR volume shifts onto slack shifts (light PID moves off), with the exact opposite swap on a tight shift — so weekly path totals never change.</p>
            <p>Uses <strong>each shift's own LVP rates</strong> and the <strong>full end-to-end (IB+DA)</strong> hours per path. AROS is capped at <strong>9,100/shift</strong>; whole cartons; best-effort (won't perfectly flatten if a path runs out of room). It <strong>populates</strong> the per-shift PID/AROS overrides for you to eyeball the Extra Hours row, then <strong>Save Plan</strong>. Existing per-shift path overrides are overwritten; per-shift hours overrides are cleared so the new mix drives hours.</p>
          </Section>
          <Section title="Rate ↔ Hours overrides (both directions)">
            <p>Overriding a function's (or dept's) <strong>Hours</strong> back-computes its <strong>Rate</strong> (= volume ÷ hours), and overriding the <strong>Rate</strong> now recomputes the <strong>Hours</strong> (= volume ÷ rate). Works for every function and department, including the ⟹ fill-across buttons. Blank a cell to release the override.</p>
          </Section>
          <Section title="Whole-number rounding (uploads)">
            <p>Uploaded <strong>planned cartons</strong> and <strong>rostered hours</strong> are rounded to the nearest whole number (the source export can carry many decimals). Rostered hours also round on load, so existing saved plans display clean.</p>
          </Section>
          <Section title="Expected / Extra / Planned hours rows">
            <p><strong>Expected Hours</strong> = Rostered × Attendance%. <strong>Planned Hours</strong> = IB + DA required hours. <strong>Extra Hours (+/-)</strong> = Expected − Planned (the VET/VTO buffer). These feed MSM and the Most Efficient Execution balancer.</p>
          </Section>
        </div>
      )}

      {/* MIDI TAB */}
      {show('midi') && (
        <div className="card" style={{ marginBottom: 12 }}>
          <h3 style={{ fontSize: '0.9rem', margin: '0 0 8px', color: '#1565c0' }}>🚛 MIDI Tab</h3>
          <Section title="MIDI Plan (SOS Freeze)">
            <p>The MIDI plan is frozen at Start of Shift (SOS) — it captures the current yard state as the plan for that shift. Trailers are sorted by dwell (oldest first) per path.</p>
            <p><strong>ISA numbers</strong> are included on plan trailers (looked up from CHECK_IN events at freeze time).</p>
          </Section>
          <Section title="FIFO in MIDI vs FALLIN">
            <p>The History tab uses MIDI plan/actual data for FIFO. The FALLIN dwell report uses per-shift logic with the full yard state. They may show different numbers because they use different methodologies.</p>
          </Section>
          <Section title="CPLH goals come from the LVP shift hours">
            <p>The CPLH/Hours goals use the LVP plan's authoritative per-shift hours: <strong>IB CPLH</strong> = total cartons ÷ LVP IB hours, <strong>OB CPLH</strong> = ÷ DA hours, <strong>Direct Throughput</strong> = (cartons × 2) ÷ Planned (IB+DA) hours, <strong>All In (Diluted)</strong> = (cartons × 2) ÷ Expected hours. (Legacy plans without those fields fall back to the older derivation.)</p>
          </Section>
          <Section title="Function Hours & Rates — plan rate uses PLANNED cartons">
            <p>The <strong>Rate Plan</strong> column divides <strong>total planned cartons</strong> for the path by planned hours — a static full-day target. It previously divided by <em>actual</em> (accruing) cartons, which halved the plan rate mid-shift and made teams look far ahead of plan (e.g. PID Unload showing ~54 instead of ~320). The <strong>Rate Actual</strong> still uses actual cartons ÷ actual hours.</p>
          </Section>
        </div>
      )}

      {/* FALLIN TAB */}
      {show('fallin') && (
        <div className="card" style={{ marginBottom: 12 }}>
          <h3 style={{ fontSize: '0.9rem', margin: '0 0 8px', color: '#1565c0' }}>🏗️ FALLIN Tab</h3>
          <Section title="Dwell Report" defaultOpen={activeTab === 'fallin'}>
            <p>Shows all IB trailer completions with dwell time, broken by path (PID/PR/MANR/SKID).</p>
            <p><strong>Date range:</strong> Configurable with quick buttons (Last 7 Days, Last 30 Days, July).</p>
            <p><strong>Shift filter:</strong> All / FH Day / FH Night / BH Day / BH Night / Wed Day / Wed Night</p>
            <p>FH = Sun/Mon/Tue, BH = Thu/Fri/Sat, Wed = Wednesday standalone.</p>
            <p><strong>FIFO %:</strong> Per-shift logic — a trailer is compliant if all older same-path trailers (6hr+ older) were also completed during the same shift. Only flags violations when older trailers are left behind.</p>
            <p><strong>Exclusions:</strong> Defect/red-tagged trailers excluded from pool. 164 chronic bypass trailers (&gt;10 times skipped) excluded. Recategorized-to-PID trailers excluded from PID FIFO count.</p>
          </Section>
          <Section title="Recategorization Columns (→PID / PID→)">
            <p><strong>→PID:</strong> Trailers originally noted as PR/MANR that were completed on PID doors (recategorized to sorter).</p>
            <p><strong>PID→:</strong> Trailers originally noted as PID that were completed on PR/MANR doors (moved off sorter).</p>
            <p><strong>Recat:</strong> Count of completions for that path where the notes didn't match the completion door.</p>
          </Section>
          <Section title="OB Trailer Fill">
            <p>Shows OB trailer completions with fill %, cubic volume, pallets/cartons.</p>
            <p><strong>Data source:</strong> FMC (fill data) joined with YMS OB_DOCK_COMPLETED (door assignment). Runs automatically via fmc-fill-sync.sh every 5 min.</p>
            <p><strong>Categories:</strong></p>
            <ul>
              <li><strong>Fluid Direct</strong> (blue): completed on DD103-DD116</li>
              <li><strong>Fluid Offline</strong> (purple): completed elsewhere, 0 pallets</li>
              <li><strong>Palletized</strong> (orange): completed elsewhere, pallets &gt; 0</li>
              <li><strong>Fluid Total:</strong> Direct + Offline combined</li>
            </ul>
            <p><strong>Avg Pallets</strong> in the TOTAL row only averages Palletized trailers (fluid always has 0).</p>
          </Section>
          <Section title="Older/bypassed trailers list (FIFO violations)">
            <p>When a completion isn't FIFO-compliant, the "✗ (N older)" list shows the older trailers it skipped. Those entries are now <strong>de-duplicated</strong> (a given trailer is listed once), and bare <strong>YMS visit-ID numbers are hidden</strong> — only trailers with a real plate/ISA are listed. The "(N older)" count still reflects the true total. This matches how MIDI avoids surfacing raw visit IDs.</p>
          </Section>
        </div>
      )}

      {/* BINKS TAB */}
      {show('binks') && (
        <div className="card" style={{ marginBottom: 12 }}>
          <h3 style={{ fontSize: '0.9rem', margin: '0 0 8px', color: '#1565c0' }}>📦 Vessel Forecast Tab</h3>
          <Section title="Total Available (SOD / EOD)">
            <p><strong>SOD (Start of Day):</strong> Available containers before that day's consumption and new-at-port arrivals are applied.</p>
            <p><strong>EOD (End of Day):</strong> After both consumption and arrivals are applied.</p>
            <p>EOD of day N = SOD of day N+1 (same running total, both snapshots exposed).</p>
          </Section>
          <Section title="Days of Backlog">
            <p>Shows EOD only. = Total Available (EOD) ÷ Containers Consumed per day.</p>
            <p>Color coding: Red &lt; 1 day, pink &lt; 2.1 days, white = healthy.</p>
          </Section>
        </div>
      )}

      {/* MSM TAB */}
      {show('msm') && (
        <div className="card" style={{ marginBottom: 12 }}>
          <h3 style={{ fontSize: '0.9rem', margin: '0 0 8px', color: '#1565c0' }}>🧩 MSM — Max Staffing Model</h3>

          <Section title="What the MSM tab does" defaultOpen={activeTab === 'msm'}>
            <p>The Max Staffing Model turns a shift's labor plan into an ideal, quarter-by-quarter staffing plan. It has two linked phases:</p>
            <p><strong>Phase 1 — Quarterly HC Forecast:</strong> takes the shift's Expected Hours and spreads them across the four quarters using the historical labor shape, giving expected hours and concurrent headcount per quarter.</p>
            <p><strong>Phase 2 — Ideal Allocation by Quarter:</strong> takes the per-quarter headcount and places every person into a specific role, level-loaded to each quarter's cap and reconciled to the LVP's planned function hours.</p>
          </Section>

          <Section title="The 4 quarters (paid clock hours)">
            <Table headers={['Quarter', 'Day window', 'Night window', 'Paid clock hrs']} rows={[
              ['Q1', '08:00–10:00', '19:00–21:00', '2.0'],
              ['Q2', '10:00–13:00', '21:00–00:00', '2.75'],
              ['Q3', '13:00–16:00', '00:00–03:00', '2.75'],
              ['Q4', '16:00–18:30', '03:00–05:30', '2.5'],
            ]} />
            <p>Paid clock hours account for the 30-min unpaid lunch (12:45–13:15, split across Q2/Q3) and the 18:30 clock-out. Total = 10 paid hrs.</p>
            <p><strong>Concurrent HC</strong> = planned hours in the quarter ÷ that quarter's paid clock hours (Q1÷2, Q2/Q3÷2.75, Q4÷2.5).</p>
          </Section>

          <Section title="Expected vs Planned Hours (LVP linkage)">
            <p><strong>Expected Hours</strong> = Rostered × Attendance (the labor you expect to show up).</p>
            <p><strong>Extra Hours (+/-)</strong> = Expected − (IB+DA); reconciled by VET/VTO before the shift starts.</p>
            <p><strong>Planned Hours</strong> = IB + DA required hours (the post-VET/VTO work). The MSM "Expected Hours" field pulls this LVP Planned Hours value, so MSM totals match the sum of IB and DA from the LVP. It's overrideable.</p>
            <p>These rows all live on the LVP tab; re-save the LVP plan so MSM can read them.</p>
          </Section>

          <Section title="BRT active-rate production model">
            <p>LVP rates are <strong>paid-hour CPLH</strong> over the 10-hour shift, but the belt only runs during BRT (belt-running time). So:</p>
            <p><strong>Active CPH = LVP CPLH ÷ belt-running fraction</strong> (BRT ÷ paid, from the historical hourly <code>schedule[].brt</code>). Example: 300 CPLH ÷ 87.5% ≈ 343 CPH.</p>
            <p><strong>Per-quarter production = crew × Active CPH × (quarter BRT minutes ÷ 60).</strong> Example: 4 assoc × 343 × (145 min ÷ 60) = 3,315 cartons.</p>
            <p>The forecast table shows a BRT (min) row per quarter and the belt-running %. The Dept Goals table shows both the LVP CPLH rate and the derived Active CPH.</p>
          </Section>

          <Section title="Dept Goals by Quarter">
            <p>Per-path carton goals (PID/Sorter, PR, AROS, PIT/SKID, Fluid-Offline, OBPR/PIT) pulled from the LVP plan, all editable. Each per-quarter cell shows the scheduled production and is overrideable (turns orange when overridden).</p>
            <p><strong>Planned</strong> column = sum of per-quarter production (green if within 10% of the Overall Goal, amber if not). The overall goal does not need to match the LVP 1:1 — the aim is the most productive plan within ±10%.</p>
            <p><strong>OBPR/PIT</strong> is spread as equally as possible across the four quarters.</p>
            <p>AROS is packed into the fewest high-capacity quarters needed to hit its goal (fixed 2-person crew when running).</p>
          </Section>

          <Section title="Toggles">
            <Table headers={['Toggle', 'Effect']} rows={[
              ['Heavy Pallets', 'Staffs IB Pallet EPJ Waterspider instead of PR Waterspider (they work in opposition).'],
              ['High Cage WIP', 'Adds 1,000 to the Fluid-Offline goal across the first half (Q1+Q2, split by capacity share).'],
              ['High Piles', 'Raises the IB PSolver minimum from 2 to 4.'],
            ]} />
          </Section>

          <Section title="How roles are staffed (allocation logic)">
            <p><strong>Primary production roles</strong> (PID/PR/AROS/PIT unloaders) are sized from carton goal ÷ Active CPH ÷ quarter BRT, in whole crews (multiples of 2, clamped to min/max).</p>
            <p><strong>Support / misc / flex roles</strong> seed their HC from the LVP planned function hours (distributed by the labor shape ÷ quarter clock hours), so hours-by-function match the LVP. This auto-handles short functions like OB PS (~3h ≈ 1 quarter).</p>
            <p><strong>Fill-to-cap:</strong> if a quarter is still under its HC cap, flex roles are upstaffed in priority order (IB PSolver → IB TDR → OB TDR → PID Inject → PID Operator → Sorter Wallbuilder → EOL Sort → Jam Clear → Pallet Build) up to their maxes. Increasing carton goals is a manual last resort, never automatic.</p>
            <p><strong>Every cell is overrideable</strong> — orange = your override, green = auto-staffed. Reconciliation row shows total HC vs cap per quarter.</p>
          </Section>

          <Section title="Fixed / conditional role rules">
            <Table headers={['Role', 'Rule']} rows={[
              ['IB Clerk', 'Fixed 1'],
              ['IB TDR', 'Fixed 1 (up to 3)'],
              ['OB TDR', 'Fixed 2 (up to 3)'],
              ['EOL Avery', 'Fixed 2'],
              ['Ship Clerk', 'Fixed 1'],
              ['IB PSolver', 'Min 2 (4 under High Piles), max 6'],
              ['Pallet Wrap/Lantech', '≥1 whenever PR is running'],
              ['AROS roles (CSX Label, Avery, Divert, Sort, Waterspider)', 'Staffed only when AROS is running; AROS Divert = 1'],
              ['Pallet Build', '1:1 to 2:1 with PR Unload'],
              ['Offline Wallbuild (Robotics Operator)', '2:1 with Fluid Load'],
              ['EOL Divert', 'Only when EOL Sort > 4'],
              ['Fluid WS', 'Only when Offline Wallbuild > 6'],
            ]} />
          </Section>

          <Section title="Allocation grid columns (Total Hrs / Exp Rate / LVP Hrs / LVP Rate)">
            <p>Each role row shows, to the right of the quarters: <strong>Total Hrs</strong> = labor hours (Σ HC × that quarter's paid clock hours), <strong>Exp Rate</strong> = dept path cartons ÷ those computed hours, <strong>LVP Hrs</strong> = the LVP planned hours for that function, and <strong>LVP Rate</strong> = dept path cartons ÷ LVP hours. Rates show one decimal.</p>
          </Section>

          <Section title="Dept Goals — Total Hrs, Rate, and rollup rows">
            <p>Dept Goals dropped the editable Rate/Active-CPH columns and added <strong>Total Hrs</strong> (all roles in that path's allocation-grid dept) and <strong>Rate</strong> (Planned ÷ Total Hrs). The Overall Goal column is now <strong>LVP Goal</strong>.</p>
            <p>Rollup rows: <strong>Total IB Cartons</strong>, <strong>Total OB Cartons</strong> (equals IB, +1,000 only under High Cage WIP), and <strong>Total Cartons</strong>. A <strong>Bottom Line</strong> row shows total IB cartons, total hours <em>including LVP admin hours</em>, and rate = Total Cartons ÷ those hours.</p>
          </Section>

          <Section title="Save Plan, zero-out, and AROS Operator">
            <p><strong>Save Plan</strong> persists your goals/rates/overrides/toggles per date + shift and reloads them next time (overlaid on the LVP defaults).</p>
            <p><strong>Zero a path for a quarter:</strong> override a path's quarter goal to 0 (PR/AROS/PIT) and that dept's roles zero out for the quarter — the freed HC redistributes to other paths via fill-to-cap.</p>
            <p><strong>AROS Operator</strong> (and Re-inject) only staff in quarters where AROS is actually running. All cells are whole numbers and grid cells display base + fill so they match the dept totals.</p>
          </Section>
        </div>
      )}

      {/* HISTORY TAB */}
      {show('history') && (
        <div className="card" style={{ marginBottom: 12 }}>
          <h3 style={{ fontSize: '0.9rem', margin: '0 0 8px', color: '#1565c0' }}>📜 History Tab</h3>
          <Section title="Trailer FIFO & Plan Compliance">
            <p><strong>FIFO%:</strong> Per path — oldest trailers processed first (uses MIDI plan/actual data, 3hr buffer).</p>
            <p><strong>Plan%:</strong> Completions that were in the frozen MIDI plan at SOS.</p>
            <p><strong>Weighted Overall:</strong> PID×10, PR×2, SKID×2, MANR×1 (invisible weights).</p>
            <p>PR/MANR/SKID completed on PID doors counts positively for both metrics (reward for running through PID).</p>
          </Section>
        </div>
      )}

      {/* INFRASTRUCTURE */}
      {show('infra') && (
        <div className="card" style={{ marginBottom: 12 }}>
          <h3 style={{ fontSize: '0.9rem', margin: '0 0 8px', color: '#1565c0' }}>⚙️ Infrastructure</h3>
          <Section title="Deploy Workflow">
            <p><strong>Frontend:</strong> <code>bash ~/shared/deploy-from-git.sh</code> — pulls from git, builds, syncs to S3 + CloudFront.</p>
            <p><strong>Backend:</strong> <code>bash ~/shared/deploy-backend-from-git.sh --restart</code> — pulls backend scripts from git, copies to live directory, restarts sync loop.</p>
            <p>Git (<code>JarJarWebsite</code> package) is the single source of truth for both.</p>
          </Section>
          <Section title="Data Sources">
            <Table headers={['Source', 'What It Provides', 'Sync Frequency']} rows={[
              ['FCLM Portal', 'Labor hours, cartons (Function Rollup CSV)', 'Every 5 min'],
              ['MonitorPortal', 'PID/AROS carton counts (source of truth)', 'Every 5 min'],
              ['YMS', 'Yard state, IB completions, check-ins, FIFO data', 'Every 5 min'],
              ['FMC', 'OB trailer fill (packages, weight, volume)', 'Every 5 min'],
              ['SSP', 'OB door assignments (VRID → door)', 'Every 5 min'],
            ]} />
          </Section>
          <Section title="Navigation — dropdown categories">
            <p>Tabs are grouped into four dropdowns (open on hover or click): <strong>Daily Execution</strong> (Hourly, MIDI, MSM, FALLIN), <strong>Weekly Planning</strong> (LVP, Vessel Forecast), <strong>Rate Planning</strong> (Bottoms Up Forecast, Tops Down Forecast, HWM Comparison), and <strong>Hatmeap</strong> (Heapmat, Hapmeat, Heatmap, History). FAQ stays standalone. The <strong>WASH</strong> and <strong>Rates</strong> tabs are hidden (the hourly is now LVP-driven).</p>
          </Section>
          <Section title="Hourly Slack posts">
            <p>The hourly dept alert posts to the Slack room at <strong>:20 past each hour</strong> via <code>slack-notify-loop.sh</code>. The bootstrap now starts (and clean-restarts) that loop alongside the sync loop, so posts survive DevSpace restarts. If it stops, it's usually because the DevSpace cycled (idle/max-runtime) and needs the bootstrap or backend deploy re-run.</p>
          </Section>
        </div>
      )}

      {/* LABOR TRACKING CODES TAB */}
      {show('laborcodes') && (
        <div style={{ marginBottom: 24 }}>
          <h3 style={{ fontSize: '0.9rem', margin: '0 0 8px', color: '#1565c0' }}>🏷️ Labor Tracking Codes</h3>
          <p style={{ fontSize: '0.8rem', color: '#555', margin: '0 0 8px' }}>Reference: process path → PPR line item → labor tracking code mapping.</p>
          <img
            src="/labor-tracking-codes.png"
            alt="Labor Tracking Codes reference"
            style={{ maxWidth: '100%', height: 'auto', border: '1px solid #ddd', borderRadius: 4 }}
          />
        </div>
      )}
      </>)}

      {/* Visible reference (outside the hidden FAQ block above). */}
      <div style={{ marginTop: 8 }}>
        <h3 style={{ fontSize: '0.9rem', margin: '0 0 8px', color: '#1565c0' }}>🏷️ Labor Tracking Codes — updated (Project Mercury)</h3>
        <p style={{ fontSize: '0.8rem', color: '#555', margin: '0 0 8px' }}>Process path → PPR line item → labor tracking code, after the Transfer Out resplit (Transfer Out Support, RSR Support, TO Fluid Load, TO RWC).</p>
        <img
          src="/labor-tracking-codes-mercury.png"
          alt="Labor Tracking Codes reference, updated for Project Mercury"
          style={{ maxWidth: '100%', height: 'auto', border: '1px solid #ddd', borderRadius: 4 }}
        />
      </div>
    </div>
  );
}
